// @ts-check
import React from 'react';
import PropTypes from 'prop-types';

import { LuBriefcaseBusiness } from 'react-icons/lu';

import { TestableSelect, TestableSelectItem, TestableText } from '@components/general/TestableComponents';

/**
 * Page header for the Business View: title, subtitle and the project picker.
 *
 * @param {Object} props
 * @param {import('./businessView.types').ProjectOption[]} props.projects
 * @param {string} props.selectedProjectId
 * @param {(id: string) => void} [props.onProjectChange]
 * @returns {JSX.Element}
 */
const BusinessViewHeader = ({ projects = [], selectedProjectId, onProjectChange }) => {
  return (
    <div className="flex flex-col gap-4" data-testid="business-view-header">
      <div className="flex flex-row items-center gap-4 pb-4 border-b border-gray-300">
				<div data-testid="bv-header" className="flex min-w-0 flex-1 flex-col items-start gap-2">
					<div className="flex flex-row items-center">
							{/*
								Colour on the wrapper, not the icon. This is a stroked icon carrying fill="none",
								and index.css gives .text-blue-500 a `fill: !important` - on the svg that beats the
								attribute and fills the outline solid. On a wrapper it only sets `color`, which the
								icon strokes with via currentColor.
							*/}
							<span className="mr-3 text-blue-500">
								<LuBriefcaseBusiness className="h-10 w-10" />
							</span>
							<TestableText dataTestId="bv-title" variant="h2" className="mr-1">Business View</TestableText>
					</div>
					<TestableText dataTestId="bv-subtitle" variant="small" className="text-gray-700 dark:text-gray-300">
							Monitor business risk, cost exposure across critical user journeys to prioritise high-impact fixes.
					</TestableText>
				</div>

				{/* Workflow picker */}
				<div className="md:w-72 lg:w-80 shrink-0">
					{/* Only mounted once the project list has loaded, so it never renders an empty picker. */}
					{projects.length > 0 && (
						<TestableSelect
							className="mt-Select-button"
							dataTestId="bv-project-select"
							value={selectedProjectId}
							onValueChange={(value) => value && onProjectChange?.(value)}
							aria-label="Select project"
						>
							{projects.map((project) => (
								<TestableSelectItem
									dataTestId={`bv-project-option-${project.id}`}
									className="mt-Select-option"
									key={project.id}
									value={project.id}
								>
									{project.label}
								</TestableSelectItem>
							))}
						</TestableSelect>
					)}
				</div>
			</div>
		</div>
  );
};

BusinessViewHeader.propTypes = {
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  selectedProjectId: PropTypes.string,
  onProjectChange: PropTypes.func,
};

export default BusinessViewHeader;
